import {
  JobFailedEv,
  JobInProgressEv,
  JobSuccessEv,
  JobState,
  JobType,
} from 'Common/types';
import { DependencyList, useEffect, useRef } from 'react';
import { filter } from 'rxjs/operators';
import { JobUpdate } from 'socketio/topics';
import { assertNever } from 'Common/utils';

export function useDidUpdateEffect(fn: () => void, inputs: DependencyList) {
  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current) {
      fn();
    } else {
      didMountRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, inputs);
}

interface UseHandleJobUpdateEventOpts {
  jobType: JobType;
  onProgress?: (msg: JobInProgressEv) => void;
  onFail?: (msg: JobFailedEv) => void;
  onSuccess?: (msg: JobSuccessEv) => void;
}

export function useHandleJobUpdateEvent({
  jobType,
  onSuccess,
  onFail,
  onProgress,
}: UseHandleJobUpdateEventOpts) {
  useEffect(() => {
    const subscription = JobUpdate.pipe(
      filter((job) => job.type === jobType)
    ).subscribe((job) => {
      switch (job.state) {
        case JobState.Completed:
          if (onSuccess) onSuccess(job);
          break;
        case JobState.Failed:
          if (onFail) onFail(job);
          break;
        case JobState.Created:
        case JobState.InProgress:
        case JobState.Queued:
          if (onProgress) onProgress(job);
          break;
        default:
          assertNever(job);
      }
    });
    return () => subscription.unsubscribe();
  }, [onSuccess, onFail, onProgress, jobType]);
  return {};
}
